import { useQueryClient } from '@tanstack/react-query';
import { Alert, Box, Button, Divider, Stack, Typography } from '@mui/material';

import { fDateTime } from 'src/utils/format-time';
import useUpdateMutation from 'src/compass/shared/hooks/reactQuery/useUpdateMutation';
import { CasSecondaryButton } from 'src/compass/shared/components/CasButton/CasSecondaryButton';
import CasButton from 'src/compass/shared/components/CasButton/CasButton';

import CasBaseDialog from '../CasDialog/CasBaseDialog';
import {
  IPendingEmail,
  PendingEmailStatus,
  PENDING_EMAILS_QUERY_KEY,
  STALE_AFTER_SECONDS,
} from './usePendingEmails';

interface Props {
  rows: IPendingEmail[];
  onClose: VoidFunction;
}

export default function CasPendingEmailsDialog({ rows, onClose }: Props) {
  const queryClient = useQueryClient();

  const { mutate } = useUpdateMutation({
    url: 'Msg/History/Acknowledge',
    // No backdrop loader: dismissing is instant and the loader drives the global dialog slot.
    showLoader: false,
    // Only invalidate. Closing is left to the pill, which drops this dialog once nothing is
    // pending - so dismissing one of several rows leaves the dialog open on the rest.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [PENDING_EMAILS_QUERY_KEY] }),
  });

  const dismiss = (ids: number[]) => mutate({ ids });

  const hasError = rows.some((row) => row.status === PendingEmailStatus.Failed);

  return (
    <CasBaseDialog
      open
      showCloseIcon
      maxWidth="sm"
      title={hasError ? 'Emails not sent' : 'Emails in progress'}
      onClose={onClose}
      // Above the pill (modal + 50), so the pill doesn't cover this dialog's own corner.
      sx={{ zIndex: (theme) => theme.zIndex.modal + 60 }}
      actions={
        <>
          <CasSecondaryButton onClick={onClose}>Close</CasSecondaryButton>
          {/* "Dismiss", never "Cancel" - this only clears the indicator, it does not stop a send
              that is already on its way. Labelled "all" because it clears every row listed,
              in-flight ones included. */}
          <CasButton onClick={() => dismiss(rows.map((row) => row.id))}>Dismiss all</CasButton>
        </>
      }
    >
      <Stack divider={<Divider flexItem />} spacing={2}>
        {rows.map((row) => {
          // Still "Sending" well after it should have finished - the row is most likely stranded
          // (Task.Run has no reconciler), so flag it rather than let it look healthy forever.
          const isStale =
            row.status !== PendingEmailStatus.Failed && row.ageSeconds > STALE_AFTER_SECONDS;

          return (
            <Box key={row.id}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                <Typography variant="subtitle2" noWrap>
                  {row.subject}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  sx={
                    isStale
                      ? { bgcolor: 'warning.lighter', px: 0.75, py: 0.25, borderRadius: 0.5 }
                      : undefined
                  }
                >
                  {/*
                    Derived from the server-computed age, NOT from row.dateTime.

                    That timestamp is stamped with DateTime.Now and serialized without a timezone
                    (see MsgHistoryPendingResponseBo), so a row written by an Azure app service -
                    which runs UTC - arrives as UTC digits that the browser then reads as local: a
                    5:26 PM send displayed as 10:26 PM. AgeSeconds is subtracted on the server
                    precisely so it carries no timezone, and anchoring it to the browser's own clock
                    gives the sender's local time in every environment.
                  */}
                  {fDateTime(new Date(Date.now() - row.ageSeconds * 1000))}
                </Typography>
              </Stack>

              <Typography variant="body2" color="text.secondary">
                To: {row.to.join(', ')}
              </Typography>

              <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                {row.status === PendingEmailStatus.Failed ? (
                  <Alert severity="error" sx={{ flexGrow: 1, py: 0 }}>
                    {row.errorMessage || 'The email could not be sent.'}
                  </Alert>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                    Sending…
                  </Typography>
                )}

                <Button size="small" color="inherit" onClick={() => dismiss([row.id])}>
                  Dismiss
                </Button>
              </Stack>
            </Box>
          );
        })}
      </Stack>
    </CasBaseDialog>
  );
}
